import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Common/Sidebar';
import { supportAPI } from '../../services/api';
import { toast } from 'react-toastify';
import {
  Ticket, Plus, Eye, Clock, CheckCircle2, AlertTriangle,
  MessageSquare, Send, XCircle, Loader2, X, Activity,
} from 'lucide-react';

/* ── design tokens ── */
const C = {
  bg:          '#f4f4f9',
  surface:     '#FFFFFF',
  border:      '#e8e8f1',
  borderSoft:  '#f0f0f6',
  ink:         '#14142b',
  body:        '#4a4a6a',
  muted:       '#6e6e86',
  primary:     '#7c3aed',
  primarySoft: '#f1ebff',
  primaryTint: '#ddd6fe',
  success:     '#10B981',
  successSoft: '#D1FAE5',
  warning:     '#F59E0B',
  warnSoft:    '#FEF3C7',
  danger:      '#EF4444',
  dangerSoft:  '#FEE2E2',
  radius:      14,
  radiusSm:    8,
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

/* ── status config ── */
const STATUS = {
  open:        { label: 'Open',        bg: C.primarySoft, color: C.primary,   Icon: Activity      },
  in_progress: { label: 'In Progress', bg: C.warnSoft,    color: '#92400E',   Icon: Clock         },
  resolved:    { label: 'Resolved',    bg: C.successSoft, color: '#065F46',   Icon: CheckCircle2  },
  closed:      { label: 'Closed',      bg: C.borderSoft,  color: C.muted,     Icon: XCircle       },
};

const PRIORITY = {
  low:    { label: 'Low',    bg: C.successSoft, color: '#065F46' },
  medium: { label: 'Medium', bg: C.warnSoft,    color: '#92400E' },
  high:   { label: 'High',   bg: '#FEE2E2',     color: '#991B1B' },
  urgent: { label: 'Urgent', bg: C.danger,      color: '#fff'    },
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.open;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, fontWeight: 700, padding: '3px 10px',
      borderRadius: 20, background: s.bg, color: s.color,
    }}>
      <s.Icon size={10} strokeWidth={2.5} />
      {s.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const p = PRIORITY[priority] || PRIORITY.medium;
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, padding: '3px 9px',
      borderRadius: 20, background: p.bg, color: p.color,
      letterSpacing: '.06em', textTransform: 'uppercase',
    }}>
      {p.label}
    </span>
  );
};

/* ── stat card ── */
const StatCard = ({ icon: Icon, iconColor, iconBg, label, value, active, onClick }) => (
  <div onClick={onClick} style={{
    background: active ? iconColor + '10' : C.surface,
    border: `1.5px solid ${active ? iconColor : C.border}`,
    borderRadius: C.radius, padding: '18px 20px',
    display: 'flex', flexDirection: 'column', gap: 12,
    boxShadow: active ? `0 4px 20px ${iconColor}20` : '0 1px 3px rgba(20,20,43,.04)',
    cursor: onClick ? 'pointer' : 'default', transition: 'all .15s',
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <p style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 6px' }}>
        {label}
      </p>
        <p style={{ fontSize: 24, fontWeight: 800, color: active ? iconColor : C.ink, margin: '4px 0 0', letterSpacing: '-0.01em', lineHeight: 1 }}>
          {value ?? 0}
        </p>
      </div>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: active ? iconColor : iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background .15s' }}>
        <Icon size={17} color={active ? '#fff' : iconColor} strokeWidth={1.8} />
      </div>
    </div>
  </div>
);

const inputSt = (focused) => ({
  width: '100%', padding: '9px 12px', fontSize: 13, color: C.ink,
  background: C.bg, border: `1.5px solid ${focused ? C.primary : C.border}`,
  borderRadius: C.radiusSm, outline: 'none', boxSizing: 'border-box',
  transition: 'border-color .15s',
});

const TH = ['Ticket ID', 'Subject', 'Priority', 'Status', 'Created', 'Action'];

const EMPTY_FORM = { subject: '', description: '', priority: 'medium' };

const UserSupport = () => {
  const [tickets,          setTickets]          = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [showCreateModal,  setShowCreateModal]  = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTicket,   setSelectedTicket]   = useState(null);
  const [replyMessage,     setReplyMessage]     = useState('');
  const [submitting,       setSubmitting]       = useState(false);
  const [focused,          setFocused]          = useState(null);
  const [formData,         setFormData]         = useState(EMPTY_FORM);
  const [formErrors,       setFormErrors]       = useState({});

  useEffect(() => { fetchTickets(); }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await supportAPI.getTickets();
      setTickets(res.data.tickets || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (formErrors[name]) setFormErrors(p => ({ ...p, [name]: '' }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.subject.trim() || formData.subject.length < 10)
      errors.subject = 'Subject must be at least 10 characters';
    if (!formData.description.trim() || formData.description.length < 20)
      errors.description = 'Description must be at least 20 characters';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await supportAPI.createTicket(formData);
      toast.success('Ticket created successfully!');
      setShowCreateModal(false);
      setFormData(EMPTY_FORM);
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetails = async (ticket) => {
    try {
      const res = await supportAPI.getTicketDetails(ticket.ticket_id);
      setSelectedTicket(res.data.ticket);
      setShowDetailsModal(true);
      setReplyMessage('');
    } catch {
      toast.error('Failed to fetch ticket details');
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) { toast.error('Please enter a message'); return; }
    setSubmitting(true);
    try {
      await supportAPI.replyToTicket(selectedTicket.ticket_id, { message: replyMessage });
      toast.success('Reply sent!');
      setReplyMessage('');
      const res = await supportAPI.getTicketDetails(selectedTicket.ticket_id);
      setSelectedTicket(res.data.ticket);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = {
    total:      tickets.length,
    open:       tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    resolved:   tickets.filter(t => t.status === 'resolved').length,
  };

  return (
    <div className="dashboard-layout">
      <Sidebar role="user" />

      <div className="dashboard-content" style={{ background: C.bg, minHeight: '100vh', padding: '28px 28px 40px' }}>

        {/* ── page header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 26, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0 }}>Support Tickets</h2>
            <p  style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Manage your support requests and get help</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '9px 18px', borderRadius: C.radiusSm,
              background: C.primary, color: '#fff',
              fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer',
            }}
          >
            <Plus size={14} strokeWidth={2.5} /> New Ticket
          </button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <Loader2 size={32} color={C.primary} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          <>
            {/* ── stat cards ── */}
            <div className="support-stat-grid">
              <StatCard icon={Ticket}       iconColor={C.primary}  iconBg={C.primarySoft}
                label="Total Tickets"  value={stats.total}      />
              <StatCard icon={Activity}     iconColor={C.primary}  iconBg={C.primarySoft}
                label="Open"           value={stats.open}       />
              <StatCard icon={Clock}        iconColor="#92400E"    iconBg={C.warnSoft}
                label="In Progress"    value={stats.inProgress} />
              <StatCard icon={CheckCircle2} iconColor="#065F46"    iconBg={C.successSoft}
                label="Resolved"       value={stats.resolved}   />
            </div>

            {/* ── tickets table ── */}
            <div style={{
              background: C.surface, border: `1px solid ${C.border}`,
              borderRadius: C.radius, padding: '20px 24px',
              boxShadow: '0 1px 3px rgba(20,20,43,.04)',
            }}>
              {tickets.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <Ticket size={44} color={C.border} strokeWidth={1} />
                  <p style={{ fontSize: 15, fontWeight: 600, color: C.ink, marginTop: 16 }}>No tickets yet</p>
                  <p style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
                    Create your first support ticket to get help from our team.
                  </p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 7,
                      padding: '10px 22px', borderRadius: C.radiusSm,
                      background: C.primary, color: '#fff',
                      fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer',
                    }}
                  >
                    <Plus size={14} strokeWidth={2.5} /> Create Your First Ticket
                  </button>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${C.borderSoft}` }}>
                        {TH.map(h => (
                          <th key={h} style={{
                            padding: '9px 14px', textAlign: 'left',
                            fontSize: 11, fontWeight: 700, color: C.muted,
                            textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map(ticket => (
                        <tr key={ticket.id}
                          style={{ borderBottom: `1px solid ${C.borderSoft}`, transition: 'background .1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = C.bg}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: C.primary }}>
                            #{ticket.ticket_id}
                          </td>
                          <td style={{ padding: '12px 14px', fontWeight: 600, color: C.ink, maxWidth: 260 }}>
                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ticket.subject}
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <PriorityBadge priority={ticket.priority} />
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <StatusBadge status={ticket.status} />
                          </td>
                          <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
                            {fmtDate(ticket.created_at)}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <button
                              onClick={() => handleViewDetails(ticket)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '5px 12px', borderRadius: C.radiusSm,
                                background: C.primarySoft, color: C.primary,
                                border: `1px solid ${C.primaryTint}`,
                                fontWeight: 600, fontSize: 12, cursor: 'pointer',
                              }}
                            >
                              <Eye size={12} strokeWidth={2} /> View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ════════ CREATE TICKET MODAL ════════ */}
      {showCreateModal && (
        <div onClick={() => !submitting && setShowCreateModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(20,20,43,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, backdropFilter: 'blur(3px)',
          }}
        >
          <div onClick={e => e.stopPropagation()}
            style={{
              background: C.surface, borderRadius: C.radius,
              width: '100%', maxWidth: 500, padding: '28px 28px 24px',
              boxShadow: '0 20px 60px rgba(20,20,43,.18)',
              maxHeight: '90vh', overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: C.primarySoft, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Plus size={17} strokeWidth={2.5} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.ink }}>Create New Ticket</p>
                  <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Response within 24 hours</p>
                </div>
              </div>
              <button onClick={() => !submitting && setShowCreateModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex' }}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateTicket}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* Subject */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>
                    Subject <span style={{ color: C.danger }}>*</span>
                  </label>
                  <input type="text" name="subject" value={formData.subject} onChange={handleInput}
                    onFocus={() => setFocused('subject')} onBlur={() => setFocused(null)}
                    placeholder="Brief description of your issue" maxLength={500}
                    style={{ ...inputSt(focused === 'subject'), borderColor: formErrors.subject ? C.danger : focused === 'subject' ? C.primary : C.border }} />
                  {formErrors.subject && (
                    <p style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>{formErrors.subject}</p>
                  )}
                  <p style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>Minimum 10 characters</p>
                </div>

                {/* Priority */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>Priority</label>
                  <select name="priority" value={formData.priority} onChange={handleInput}
                    style={{ ...inputSt(false), height: 38 }}>
                    <option value="low">Low — General inquiry</option>
                    <option value="medium">Medium — Normal issue</option>
                    <option value="high">High — Important issue</option>
                    <option value="urgent">Urgent — Critical issue</option>
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>
                    Description <span style={{ color: C.danger }}>*</span>
                  </label>
                  <textarea name="description" value={formData.description} onChange={handleInput}
                    onFocus={() => setFocused('desc')} onBlur={() => setFocused(null)}
                    rows={4} placeholder="Please describe your issue in detail…"
                    style={{ ...inputSt(focused === 'desc'), resize: 'vertical', borderColor: formErrors.description ? C.danger : focused === 'desc' ? C.primary : C.border }} />
                  {formErrors.description && (
                    <p style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>{formErrors.description}</p>
                  )}
                  <p style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>Minimum 20 characters</p>
                </div>

                {/* info note */}
                <div style={{
                  background: C.primarySoft, border: `1px solid ${C.primaryTint}`,
                  borderRadius: C.radiusSm, padding: '10px 14px',
                  fontSize: 12, color: C.body,
                }}>
                  <strong style={{ color: C.primary }}>Note:</strong> Our support team will respond within 24 hours. You'll receive email notifications for updates.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button type="button" disabled={submitting} onClick={() => setShowCreateModal(false)}
                  style={{ flex: 1, padding: '10px 0', borderRadius: C.radiusSm, background: C.bg, color: C.body, border: `1px solid ${C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: C.radiusSm,
                    background: submitting ? C.muted : C.primary, color: '#fff', border: 'none',
                    fontWeight: 700, fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  }}>
                  {submitting
                    ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Creating…</>
                    : <><Plus size={14} strokeWidth={2.5} /> Create Ticket</>
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════ TICKET DETAILS MODAL ════════ */}
      {showDetailsModal && selectedTicket && (
        <div onClick={() => setShowDetailsModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(20,20,43,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, backdropFilter: 'blur(3px)',
          }}
        >
          <div onClick={e => e.stopPropagation()}
            style={{
              background: C.surface, borderRadius: C.radius,
              width: '100%', maxWidth: 580, padding: '28px 28px 24px',
              boxShadow: '0 20px 60px rgba(20,20,43,.18)',
              maxHeight: '88vh', display: 'flex', flexDirection: 'column',
            }}
          >
            {/* header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexShrink: 0 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 800, color: C.primary }}>
                    #{selectedTicket.ticket_id}
                  </span>
                  <StatusBadge status={selectedTicket.status} />
                  <PriorityBadge priority={selectedTicket.priority} />
                </div>
                <p style={{ fontSize: 15, fontWeight: 700, color: C.ink, margin: 0 }}>{selectedTicket.subject}</p>
                <p style={{ fontSize: 11, color: C.muted, margin: '4px 0 0' }}>
                  Created {fmtDate(selectedTicket.created_at)}
                  {selectedTicket.resolved_at && ` · Resolved ${fmtDate(selectedTicket.resolved_at)}`}
                </p>
              </div>
              <button onClick={() => setShowDetailsModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex', flexShrink: 0 }}>
                <X size={18} />
              </button>
            </div>

            {/* description */}
            <div style={{
              background: C.bg, border: `1px solid ${C.border}`,
              borderRadius: C.radiusSm, padding: '12px 14px', marginBottom: 16,
              fontSize: 13, color: C.body, lineHeight: 1.6, flexShrink: 0,
            }}>
              {selectedTicket.description}
            </div>

            {/* conversation */}
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <MessageSquare size={13} color={C.primary} strokeWidth={2} />
                <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em' }}>
                  Conversation
                </span>
              </div>

              {selectedTicket.replies && selectedTicket.replies.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {selectedTicket.replies.map((reply, i) => (
                    <div key={i} style={{
                      padding: '12px 14px',
                      borderRadius: C.radiusSm,
                      background: reply.is_admin_reply ? C.primarySoft : C.bg,
                      border: `1px solid ${reply.is_admin_reply ? C.primaryTint : C.border}`,
                      marginLeft: reply.is_admin_reply ? 0 : 24,
                      marginRight: reply.is_admin_reply ? 24 : 0,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: reply.is_admin_reply ? C.primary : C.ink }}>
                          {reply.is_admin_reply ? '🛡 Support Team' : '👤 You'}
                        </span>
                        <span style={{ fontSize: 11, color: C.muted }}>{fmtDate(reply.created_at)}</span>
                      </div>
                      <p style={{ fontSize: 13, color: C.body, margin: 0, lineHeight: 1.5 }}>{reply.message}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  background: C.bg, border: `1px solid ${C.border}`,
                  borderRadius: C.radiusSm, padding: '16px 14px',
                  fontSize: 13, color: C.muted, textAlign: 'center',
                }}>
                  No replies yet — our support team will respond soon.
                </div>
              )}
            </div>

            {/* reply form or closed */}
            <div style={{ flexShrink: 0 }}>
              {selectedTicket.status !== 'closed' ? (
                <form onSubmit={handleSendReply}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Send size={12} strokeWidth={2} color={C.primary} /> Add Reply
                  </label>
                  <textarea
                    value={replyMessage} onChange={e => setReplyMessage(e.target.value)}
                    onFocus={() => setFocused('reply')} onBlur={() => setFocused(null)}
                    rows={3} placeholder="Type your message here…"
                    style={{ ...inputSt(focused === 'reply'), resize: 'none', marginBottom: 10 }}
                  />
                  <button type="submit" disabled={submitting || !replyMessage.trim()}
                    style={{
                      width: '100%', padding: '10px 0', borderRadius: C.radiusSm,
                      background: submitting || !replyMessage.trim() ? C.muted : C.primary,
                      color: '#fff', border: 'none', fontWeight: 700, fontSize: 13,
                      cursor: submitting || !replyMessage.trim() ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    }}>
                    {submitting
                      ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Sending…</>
                      : <><Send size={13} strokeWidth={2} /> Send Reply</>
                    }
                  </button>
                </form>
              ) : (
                <div style={{
                  background: C.borderSoft, border: `1px solid ${C.border}`,
                  borderRadius: C.radiusSm, padding: '12px 14px',
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 13, color: C.muted,
                }}>
                  <XCircle size={14} strokeWidth={2} />
                  This ticket is closed — no further replies can be added.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        .support-stat-grid {
          display: grid;
          grid-template-columns: repeat(4,1fr);
          gap: 18px;
          margin-bottom: 24px;
        }
        @media (max-width: 700px) {
          .support-stat-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 400px) {
          .support-stat-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default UserSupport;
